import { FC, useCallback } from 'react';
import type { UserProject } from '@teachur/http-client';
import { SortableTable, SortableTableColumn } from '@new/common/ui/Table/SortableTable';
import { Button } from '@new/common/ui/Button/Button';
import { useProjectsReview, PAGE_SIZE } from '../../contexts/ProjectsReviewProvider';
import {
  formatCourseNames,
  formatSubmittedDate,
  formatFullName,
} from '../../utils/projectReviewFormatters';
import { ProjectStatusBadge } from '../ProjectStatusBadge/ProjectStatusBadge';
import { getLatestSubmission } from '../../utils/projectReviewDrawerHelpers';

const DUE_DAYS = 5;
const GRACE_DAYS = 2;

const getDaysLeft = (project: UserProject): number | null => {
  if (!project.claimedAt) return null;
  if (project.status === 'approved' || project.status === 'notYet') return null;
  const claimedDate = new Date(project.claimedAt);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - claimedDate.getTime()) / (1000 * 60 * 60 * 24));
  return DUE_DAYS + GRACE_DAYS - diffDays;
};

const DaysLeftCell: FC<{ project: UserProject }> = ({ project }) => {
  const daysLeft = getDaysLeft(project);

  if (daysLeft === null)
    return <span className="text-sm text-[var(--neutral-black-NB400)]">—</span>;

  const isOverdue = daysLeft < 0;
  const isLate = daysLeft >= 0 && daysLeft < GRACE_DAYS;

  const colorClass = isOverdue
    ? 'bg-[var(--danger-D100)] text-[var(--danger-D700)] border border-[var(--danger-D300)]'
    : isLate
      ? 'bg-[var(--warning-W100)] text-[var(--warning-W700)] border border-[var(--warning-W300)]'
      : 'text-[var(--neutral-black-NB700)]';

  const label = isOverdue ? `${daysLeft}d` : `${daysLeft}d`;

  return (
    <span
      className={`inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold ${colorClass}`}
    >
      {label}
    </span>
  );
};

export const ProjectsTable: FC = () => {
  const { projects, selectedProject, page, setPage, total, setSelectedProjectId } =
    useProjectsReview();
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const getRowClassName = useCallback((project: UserProject): string => {
    const daysLeft = getDaysLeft(project);
    if (daysLeft === null) return '';
    if (daysLeft < 0) return 'bg-[var(--danger-D200)]!';
    if (daysLeft < GRACE_DAYS) return 'bg-[var(--warning-W200)]!';
    return '';
  }, []);

  const columns: SortableTableColumn<UserProject>[] = [
    {
      key: 'user',
      header: 'Student',
      sortable: true,
      className: 'max-w-[150px] truncate',
      render: (_: unknown, row: UserProject) =>
        formatFullName(row.user?.firstName, row.user?.lastName, '—'),
    },
    {
      key: 'project',
      header: 'Project',
      sortable: true,
      className: 'max-w-[180px] truncate',
      render: (_: unknown, row: UserProject) => row.project?.name ?? '—',
    },
    {
      key: 'course',
      header: 'Course',
      sortable: true,
      className: 'max-w-[180px] truncate',
      render: (_: unknown, row: UserProject) => formatCourseNames(row.project?.courses),
    },
    {
      key: 'instructor',
      header: 'Instructor',
      sortable: true,
      className: 'max-w-[150px] truncate',
      render: (_: unknown, row: UserProject) =>
        row.instructor
          ? formatFullName(row.instructor.firstName, row.instructor.lastName, '—')
          : '—',
    },
    {
      key: 'createdAt',
      header: 'Submitted',
      sortable: true,
      className: 'max-w-[120px] truncate',
      render: (_: unknown, row: UserProject) =>
        formatSubmittedDate(getLatestSubmission(row)?.createdAt ?? row.createdAt),
    },
    {
      key: 'claimedAt',
      header: 'Claimed',
      sortable: true,
      className: 'max-w-[120px] truncate',
      render: (_: unknown, row: UserProject) =>
        row.claimedAt ? formatSubmittedDate(row.claimedAt) : '—',
    },
    {
      key: 'daysLeft',
      header: 'Days Left',
      sortable: false,
      className: 'max-w-[100px] truncate',
      render: (_: unknown, row: UserProject) => <DaysLeftCell project={row} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      className: 'max-w-[120px] truncate',
      render: (_: unknown, row: UserProject) => <ProjectStatusBadge status={row.status} />,
    },
  ];

  return (
    <div className="flex flex-col">
      <SortableTable<UserProject>
        data={projects}
        columns={columns}
        getRowKey={(project) => project.id}
        emptyMessage="No projects found"
        emptyDescription="Try adjusting your search or filter criteria."
        onRowClick={(project) => setSelectedProjectId(project.id)}
        isRowSelected={(project) => selectedProject?.id === project.id}
        getRowClassName={getRowClassName}
      />
      <div className="flex items-center gap-4 px-6 py-2 border-t border-[#e1e7ea]">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm bg-[var(--warning-W200)]" />
          <span className="text-xs text-[var(--neutral-black-NB600)]">Late</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm bg-[var(--danger-D200)]" />
          <span className="text-xs text-[var(--neutral-black-NB600)]">Overdue</span>
        </div>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-3 border-t border-[#e1e7ea]">
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="neutral"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="neutral"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
